import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { projectsService, type ProjectQueryParams, type Project } from '../../services/projects.service';
import { partnersService } from '../../services/partners.service';
import type { Partner } from '../../types/partners';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { formatDate } from 'sharedUi/dateUtils';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Name', filter: true },
  { field: 'appCode', header: 'Project Code', filter: true },
];

const ProjectsList = () => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'apps',
    basePath: '/projects',
  });
  const toast = useRef<Toast>(null);
  /* The owning partner, resolved id -> name. The list endpoint returns
     `partnerMasterId` only, so the names are fetched once and looked up per row
     rather than per-row requests. Best-effort: a failure leaves the column
     showing the dash, it never blocks the grid. */
  const [partners, setPartners] = useState<Partner[]>([]);
  useEffect(() => {
    let cancelled = false;
    partnersService.getAllForDropdown()
      .then((rows) => { if (!cancelled) setPartners(rows); })
      .catch(() => { /* column falls back to '-' */ });
    return () => { cancelled = true; };
  }, []);
  const partnerName = useCallback(
    (id?: string | null) => (id ? partners.find((x) => x._id === id)?.name || '-' : '-'),
    [partners],
  );
  const canCreate = useHasPermission(PERMISSIONS.APP_CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.APP_UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.APP_DELETE);

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchProjects = useCallback(
    async (
      page = 1,
      size = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);
        const params: ProjectQueryParams = {
          page,
          page_size: size,
          include_deleted: includeDeleted,
        };

        if (debouncedSearchText && debouncedSearchText.trim()) {
          params.search = debouncedSearchText.trim();
        }
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        const response = await projectsService.getAll(params);
        const mappedData = (response.data || []).map((item: any) => ({ ...item, id: item._id }));
        setProjects(mappedData);

        const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
        setTotalRecords(total);
      } catch (err: any) {
        console.error('Failed to fetch projects:', err);
        showError(extractErrorMessage(err, 'Failed to load projects. Please try again.'));
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, debouncedSearchText, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchProjects(newPage, event.rows, sortField, sortOrder);
  };

  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchProjects(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchProjects(1, pageSize, sortField, sortOrder, next);
  };

  useEffect(() => {
    setFirst(0);
    fetchProjects(1, pageSize);
  }, [includeDeleted, debouncedSearchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string) => navigateToView(id);
  const handleEdit = (id: string) => navigateToEdit(id);

  const handleDeleteClick = (id: string) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;
    try {
      setDeleteLoading(true);
      await projectsService.delete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchProjects(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (err: any) {
      console.error('Failed to delete project:', err);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(err, 'Failed to delete project'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const columns: SaasColumn<Project>[] = useMemo(() => {
    const cols: SaasColumn<Project>[] = [
      { field: 'name', header: 'Name', sortable: true, filter: true, hideable: false },
      { field: 'appCode', header: 'Project Code', sortable: true, filter: true, body: (r) => r.appCode || '-' },
      // Only a CUSTOMER partner can own a project; a project with none shows '-'.
      { field: 'partnerMasterId', header: 'Partner', body: (r) => partnerName(r.partnerMasterId) },
      { field: 'startAt', header: 'Start Date', sortable: true, body: (r) => (r.startAt ? formatDate(r.startAt) : '-') },
      {
        field: 'paymentDone',
        header: 'Payment',
        body: (r) => (
          <span
            className={`saas-status-dot ${r.paymentDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
          >
            {r.paymentDone ? 'Paid' : 'Not paid'}
          </span>
        ),
      },
      { field: 'createdAt', header: 'Created At', sortable: true, body: (r) => formatDate(r.createdAt) },
    ];

    if (includeDeleted) {
      cols.push({
        field: 'status',
        header: 'Status',
        body: (r) => {
          const deleted = (r as any).isDelete || r.isDeleted;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (r) => {
        const deleted = (r as any).isDelete || r.isDeleted;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(r._id)}
              data-testid="AppsList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
            </button>
            {!deleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(r._id)}
                    data-testid="AppsList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(r._id)}
                    data-testid="AppsList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, includeDeleted, partnerName]);

  return (
    <PermissionGuard permission={PERMISSIONS.APP_READ}>
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-Projects-List">
        <PageHeader
          icon="pi pi-th-large"
          title="Projects"
          subtitle="Manage projects"
          testId="Projects.Header"
        />

        <div className="ds-card overflow-hidden">
          <SaasDataTable
            tableId="projects"
            columns={columns}
            data={projects}
            loading={loading}
            dataKey="_id"
            emptyMessage="No projects found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search projects..."
            testId="ProjectsList"
            toolbarStart={
              undefined
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchProjects(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                />
                {canCreate && (
                  <Button
                    label="New Project"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="ProjectsList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this project? This will mark it as deleted."
          testId="ProjectsList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default ProjectsList;
